// RegisterPage.js
import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from "../utils/api";
import { resolveSetJansForItems } from "../utils/smartDetaxSetJans";
import { ENABLE_SMARTDETAX_LEGACY_FEATURES } from "../utils/smartDetaxQr";
import { useNavigate } from 'react-router-dom';
import BarcodeInputForm from '../components/BarcodeInputForm';
import SalesDetailList from '../components/SalesDetailList';
import PaymentSummary from '../components/PaymentSummary';
import NonStockTaxItemInput from "../components/NonStockTaxItemInput";
import PaymentActionsWithKeypad from '../components/PaymentActionsWithKeypad'; 
import CashTransactionModal from '../components/CashTransactionModal';
import CurrentTime from '../components/CurrentTime';
import ReturnModal from '../components/ReturnModal';
import {
  printReceipt,
  prepareSmartDetaxReceipt,
  getSmartDetaxImages,
  printSmartDetaxReceipt,
} from '../components/ReceiptPrinter';
import { useAuthContext } from "../context/AuthContext"; 
import { useAlert } from '../components/AlertModal'; 



import '../styles/RegisterPage.css';
import '../styles/RegisterSmartDetax.css';
import '../styles/TaxFreeMinimumNotice.css';

const RegisterPage = () => {
  const [salesList, setSalesList] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [sale_type, setSale_type] = useState('normal');
  const [cashGiven, setCashGiven] = useState(0);
  const [cardGiven, setCardGiven] = useState(0);
  const [change, setChange] = useState(0);
  const [taxRate, setTaxRate] = useState(null);
  const [taxFreeMode, setTaxFreeMode] = useState("legacy");
  // 同日・同一店舗・同じお客様の既存免税購入に追加する場合だけスタッフが明示的に解除する。
  const [taxFreeMinimumOverride, setTaxFreeMinimumOverride] = useState(false);
  // 「免税小計」を押したときにだけ不足額の案内を開始する。
  const [taxFreeSubtotalSelected, setTaxFreeSubtotalSelected] = useState(false);
  const [taxFreeModeLoading, setTaxFreeModeLoading] = useState(true);
  const [changeinfo, setChangeinfo] = useState(""); 
  const [tax_exemption, setTax_exemption] = useState(0); 
  const [selectedRegister, setSelectedRegister] = useState(null);  // レジ選択
  const [storeId, setStoreId] = useState(null);  // 店舗選択
  const { user: userInfo, loading } = useAuthContext(); // Contextから取得
  const [input, setInput] = useState(''); // キーパッドの入力値
  const [selectedItem, setSelectedItem] = useState(null); // 編集中の商品情報
  const [showModal, setShowModal] = useState(false);  // モーダル表示の状態
  const inputRef = useRef(null);
  const [showCashTransactionModal, setShowCashTransactionModal] = useState(false);  // モーダル表示の状態
  const [currentTime, setCurrentTime] = useState(new Date()); // 親でcurrentTimeを管理
  const [showReturnModal, setShowReturnModal] = useState(false); // 返品モーダル用
  const navigate = useNavigate(); // 取引履歴用
  const [receiptNo, setReceiptNo] = useState(0);
  const { showAlert, closeAlert } = useAlert(); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  // SET構成品の取得・QR事前チェック中の連打による二重checkoutを防止する
  const checkoutInProgressRef = useRef(false);
  // 免税のQR上限警告は超過したときだけ1回表示する。
  const qrLimitWarnedRef = useRef(false);
  const [submittedReceiptId, setSubmittedReceiptId] = useState(null);
  const [barcodeFocusEnabled, setBarcodeFocusEnabled] = useState(true);
  const [lastReceiptData, setLastReceiptData] = useState(null);
  // 会計完了画面は共通のAlertModalに統合。QRを4個ずつ表示。
  const [smartDetaxPrinting, setSmartDetaxPrinting] = useState(false);

  // 免税下限：税抜5,000円（画面表示／会計前チェックに共通利用）
  const TAX_FREE_MINIMUM_EXCL_TAX = 5000;
  // subtotal は通常税抜。内税設定なら内税相当分を差し引いて判定する。
  const taxFreeNetAmount = Math.max(0, Number(totalAmount || 0) -
    (taxRate?.tax_type === "inclusive"
      ? Math.floor(Number(totalAmount || 0) * Number(taxRate.tax_rate) /
          (100 + Number(taxRate.tax_rate)))
      : 0));
  const taxFreeRemaining = Math.max(0, TAX_FREE_MINIMUM_EXCL_TAX - taxFreeNetAmount);
  const showTaxFreeMinimumWarning =
    sale_type === "tax_exempt" &&
    salesList.length > 0 &&
    submittedReceiptId === null &&
    taxFreeRemaining > 0 &&
    taxFreeSubtotalSelected &&
    !taxFreeMinimumOverride;

  // PaymentActionsWithKeypadの既存「小計」「免税小計」が呼ぶsetSale_typeを受ける。
  // 別の判定用ボタンは追加しない。押すたびに同じ免税小計を再判定できる。
  const handleSubtotalSelection = (nextSaleType) => {
    const next = typeof nextSaleType === "function"
      ? nextSaleType(sale_type)
      : nextSaleType;

    if (next === "tax_exempt") {
      // 免税小計：税抜5,000円未満のときだけ既存の注意ポップアップが出る。
      setTaxFreeMinimumOverride(false);
      setTaxFreeSubtotalSelected(salesList.length > 0);
    } else {
      // 通常の小計：免税警告を双方の画面から消して通常会計へ。
      setTaxFreeMinimumOverride(false);
      setTaxFreeSubtotalSelected(false);
    }
    setSale_type(next);
  };

  // 同日追加購入として進む（免税のまま・会計確定はまだしない）。
  // これにより客面のポップアップも同期後に消える。
  const handleTaxFreeAdditionalPurchase = () => {
    setTaxFreeMinimumOverride(true);
    setTaxFreeSubtotalSelected(false);
  };

  // いったん税抜5,000円に達したら案内を終了する。
  // その後5,000円未満に戻っても、次に「免税小計」を押すまでは再表示しない。
  useEffect(() => {
    if (sale_type !== "tax_exempt" || taxFreeRemaining <= 0) {
      setTaxFreeSubtotalSelected(false);
    }
    if (sale_type !== "tax_exempt") {
      setTaxFreeMinimumOverride(false);
    }
  }, [sale_type, taxFreeRemaining]);

  const [autoReceiptPrintEnabled, setAutoReceiptPrintEnabled] = useState(() => {
    return localStorage.getItem("autoReceiptPrintEnabled") === "true";
  });
   
  // 日付と曜日を取得
  const dayOfWeek = currentTime.toLocaleString('ja-JP', { weekday: 'long' }); // 曜日
  const formattedDate = currentTime.toLocaleDateString('ja-JP'); // 日付
  const formattedTime = currentTime.toLocaleTimeString('ja-JP'); // 時間


  const lastReceiptAlertActiveRef = useRef(false);
  const completedDisplayRef = useRef(null);

  const staffResetTimerRef = useRef(null);
  const paymentCompletedHoldRef = useRef(false);

  useEffect(() => {
    if (userInfo?.register_id) {
      setSelectedRegister(userInfo.register_id);
      setStoreId(userInfo.store_id);
    }
  }, [userInfo]);

  useEffect(() => {
    localStorage.setItem(
      "autoReceiptPrintEnabled",
      autoReceiptPrintEnabled
    );
  }, [autoReceiptPrintEnabled]);

  // 自動フォーカス
  useEffect(() => {

    // 読み込み中・フォーカス無効・モーダル表示中は処理しない
    if (
      loading ||
      taxFreeModeLoading ||
      !barcodeFocusEnabled ||
      showModal ||
      showCashTransactionModal ||
      showReturnModal
    ) {
      return;
    }

    // バーコード入力欄へフォーカスを戻す
    const focusBarcode = () => {

      // ダイアログ表示中はフォーカスを奪わない
      const modalOpen = document.querySelector(
        '[role="dialog"], [aria-modal="true"], .ReactModal__Overlay'
      );

      if (modalOpen) return;

      if (
        inputRef.current &&
        document.activeElement !== inputRef.current
      ) {
        inputRef.current.focus();
      }
    };

    // 初回表示・モーダル終了時
    const initialTimer = setTimeout(focusBarcode, 0);

    let focusTimer = null;

    const handler = (e) => {

      // 他の入力欄を操作している場合はフォーカスを奪わない
      if (
        e.target.closest?.(
          'input, textarea, select, [contenteditable="true"]'
        )
      ) {
        return;
      }

      clearTimeout(focusTimer);

      focusTimer = setTimeout(() => {
        focusBarcode();
      }, 10);
    };

    // クリック時にフォーカスを戻す
    document.addEventListener("click", handler);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(focusTimer);
      document.removeEventListener("click", handler);
    };

  }, [
    loading,
    taxFreeModeLoading,
    barcodeFocusEnabled,
    showModal,
    showCashTransactionModal,
    showReturnModal
  ]);

  const handleSmartDetaxPrint = async (receipt) => {
    if (smartDetaxPrinting || !receipt?.isTaxFree) return;
    if (receipt.smartDetaxPreparationError) {
      showAlert(`SmartDetaxのQRを作成できません：${receipt.smartDetaxPreparationError}`);
      return;
    }
    setSmartDetaxPrinting(true);
    try {
      await printSmartDetaxReceipt(receipt, showAlert);
    } finally {
      setSmartDetaxPrinting(false);
    }
  };

  const resetAfterPaymentHold = () => {
    if (!paymentCompletedHoldRef.current) return;

    paymentCompletedHoldRef.current = false;

    if (staffResetTimerRef.current) {
      clearTimeout(staffResetTimerRef.current);
      staffResetTimerRef.current = null;
    }

    resetRegisterOnly();
  };

  useEffect(() => {
    const closeOnInput = () => {
      if (!lastReceiptAlertActiveRef.current) return;

      closeAlert();
      lastReceiptAlertActiveRef.current = false;

      resetAfterPaymentHold();
    };

    window.addEventListener("keydown", closeOnInput);
    window.addEventListener("input", closeOnInput);
    window.addEventListener("click", closeOnInput);

    return () => {
      window.removeEventListener("keydown", closeOnInput);
      window.removeEventListener("input", closeOnInput);
      window.removeEventListener("click", closeOnInput);;
    };
  }, [closeAlert]);

  const handleButtonfocusClick = () => {
  // ボタンをクリックした時に子のinputにフォーカスを当てる
    if (inputRef.current) {
      inputRef.current.focus();
    }else{}
  };

  const regicancel = () => {
  completedDisplayRef.current = null;
  setTaxFreeMinimumOverride(false);
  setTaxFreeSubtotalSelected(false);

    setSalesList([]);  // 販売明細をリセット
    setCashGiven(0); // 現金額をリセット
    setPaymentMethod(""); // 支払い方法をリセット
    setTotalAmount(0); // 合計金額をリセット
    setSale_type('normal'); // 免税判定をリセット
    setCardGiven(0); // キャッシュレス預りをリセット
    setChange(0); // お釣りリセット
    setChangeinfo(0); // お釣りディスプレイをリセット
    setTax_exemption(0); // 免税額をリセット
    setSubmittedReceiptId(null);
    handleButtonfocusClick(); // フォーカス
    localStorage.setItem(
      "customerDisplayData",
      JSON.stringify({
        status: "standby",
        items: [],
        subtotal: 0,
        tax: 0,
        total: 0,
        paidCash: 0,
        paidCashless: 0,
        change: 0,
        message: "いらっしゃいませ",
      })
    );  
  } 

  const resetRegisterOnly = () => {
    setTaxFreeMinimumOverride(false);
    setTaxFreeSubtotalSelected(false);
    setSalesList([]);
    setCashGiven(0);
    setPaymentMethod("");
    setTotalAmount(0);
    setSale_type("normal");
    setCardGiven(0);
    setChange(0);
    setChangeinfo(0);
    setTax_exemption(0);
    setSubmittedReceiptId(null);
    handleButtonfocusClick();
  };

  const clearCompletedPaymentForNextSale = () => {
    if (!paymentCompletedHoldRef.current) return false;

    paymentCompletedHoldRef.current = false;

    if (staffResetTimerRef.current) {
      clearTimeout(staffResetTimerRef.current);
      staffResetTimerRef.current = null;
    }

    return true;
  };

  useEffect(() => {
    fetchTaxRate();
    fetchTaxFreeMode();
  }, []);

  useEffect(() => {
      const total = salesList.reduce((sum, item) => sum + item.subtotal, 0);
      setTotalAmount(total);
    }, [salesList]);

  useEffect(() => {
    // 会計完了後、salesList が空になっても客画面を消さない
    // 次の商品が入ったら通常表示で上書き
    if (salesList.length === 0 && completedDisplayRef.current) {
      localStorage.setItem(
        "customerDisplayData",
        JSON.stringify(completedDisplayRef.current)
      );
      return;
    }

    // 通常の空状態は待機画面
    if (salesList.length === 0) {
      localStorage.setItem(
        "customerDisplayData",
        JSON.stringify({
          status: "standby",
          saleType: sale_type,
          isTaxFree: sale_type === "tax_exempt",
          taxFreeMinimumWarning: null,
          items: [],
          subtotal: 0,
          discountTotal: 0,
          tax: 0,
          taxBeforeExemption: 0,
          taxIncludedSubtotal: 0,
          taxExemptionAmount: 0,
          total: 0,
          paidCash: 0,
          paidCashless: 0,
          change: 0,
        })
      );
      return;
    }

    // 次の商品が入ったら完了表示を解除
    completedDisplayRef.current = null;

    const taxBeforeExemption = calculateTax(totalAmount, taxRate);
    const tax = calculateSaleTax(totalAmount);

    const discountTotal = salesList.reduce(
      (sum, item) => sum + Number(item.discount || 0),
      0
    );

    const customerDisplayData = {
      status: salesList.length > 0 ? "selling" : "standby",
      saleType: sale_type,
      isTaxFree: sale_type === "tax_exempt",
      taxFreeMode: taxFreeMode,
      // 「免税小計」を押して税抜5,000円未満の場合、お客様側の案内に使用。
      taxFreeMinimumWarning: showTaxFreeMinimumWarning
        ? { minimum: TAX_FREE_MINIMUM_EXCL_TAX, current: taxFreeNetAmount, remaining: taxFreeRemaining }
        : null,

      items: salesList.map((item) => ({
        name: item.product_name,
        quantity: item.quantity,
        price: item.unit_price,
        subtotal: item.subtotal,
        discount: item.discount || 0,
        jan: item.barcode || item.jan || "",
      })),

      subtotal: totalAmount,
      discountTotal,

      tax,

      taxBeforeExemption,

      taxIncludedSubtotal:
        totalAmount + taxBeforeExemption,

      taxExemptionAmount:
        sale_type === "tax_exempt" &&
        taxFreeMode === "legacy"
          ? taxBeforeExemption
          : 0,

      total: totalAmount + tax,

      paidCash: cashGiven,
      paidCashless: cardGiven,
      change,
    };

    localStorage.setItem(
      "customerDisplayData",
      JSON.stringify(customerDisplayData)
    );
  }, [
    salesList,
    totalAmount,
    sale_type,
    taxRate,
    taxFreeMode,
    cashGiven,
    cardGiven,
    change,
    taxFreeMinimumOverride,
    taxFreeSubtotalSelected,
    taxFreeNetAmount,
    taxFreeRemaining,
    showTaxFreeMinimumWarning,
  ]);

  const fetchTaxRate = async () => {
    try {
      const data = await apiRequest('inventory/tax_rates/', 'GET');
      if (data && data.tax_rate) {
        setTaxRate(data);
      } else {
        setTaxRate({ tax_rate: 10.00, tax_type: 'exclusive' });
      }
    } catch (error) {
      console.error('税率の取得に失敗しました:', error);
      setTaxRate({ tax_rate: 10.00, tax_type: 'exclusive' });
    }
  };

  const fetchTaxFreeMode = async () => {
    try {
      const data = await apiRequest(
        "inventory/system-setting/",
        "GET"
      );

      setTaxFreeMode(data?.tax_free_mode || "legacy");
    } catch (error) {
      console.error("免税方式の取得に失敗しました:", error);

      // 取得失敗時は安全のため現在方式
      setTaxFreeMode("legacy");
    } finally {
      setTaxFreeModeLoading(false);
    }
  };

  const calculateTax = (subtotal, taxRate) => {
    if (taxRate) {
      if (taxRate.tax_type === 'inclusive') {
        return Math.floor(subtotal * (taxRate.tax_rate / (100 + taxRate.tax_rate)));
      } else {
        return Math.floor(subtotal * (taxRate.tax_rate / 100));
      }
    }
    return 0;
  };

  const calculateSaleTax = (subtotal) => {
    const tax = calculateTax(subtotal, taxRate);

    // 従来方式の免税だけ税0円
    if (
      sale_type === "tax_exempt" &&
      taxFreeMode === "legacy"
    ) {
      return 0;
    }

    // 通常販売、またはリファンド方式の免税
    return tax;
  };

  // 販売明細リストの追加
  const addToSalesList = (item) => {
    const wasHolding = clearCompletedPaymentForNextSale();

    if (wasHolding) {
      setTaxFreeMinimumOverride(false);
      setTaxFreeSubtotalSelected(false);
      setCashGiven(0);
      setPaymentMethod("");
      setTotalAmount(0);
      setSale_type("normal");
      setCardGiven(0);
      setChange(0);
      setChangeinfo(0);
      setTax_exemption(0);
      setSubmittedReceiptId(null);

      // 前会計の商品を消して、新しい商品だけで開始
      setSalesList([item]);
    } else {
      setSalesList((prevList) => [...prevList, item]);
    }

    handleButtonfocusClick();
  };

  // 販売明細リストの更新
  const updateSalesDetail = (updatedList) => {
      setSalesList(updatedList);
      handleButtonfocusClick();
  };

  // 販売明細リストから商品を削除
  const removeSalesDetail = (updatedList) => {
      setSalesList(updatedList);
      handleButtonfocusClick();
  };
  
  // 取引履歴を開く
  const handleViewTransactionHistory = () => {
        navigate('/transaction_history');  // リストページに遷移
    };

  // モーダルを開く
  const openModal = (item) => {
      setSelectedItem(item);  // 編集するアイテムを設定
      setShowModal(true);  // モーダルを表示
  }; 

  // モーダルを開く
  const openCashTransactionModal = () => {
      if (!input || isNaN(input) || input <= 0) {
        showAlert("金額を入力してください");
        return;
      }
      else{
        setShowCashTransactionModal(true);  // モーダルを表示
      }
  }; 
  // モーダルを閉じる
  const closeCashTransactionModal  = () => {
      setShowCashTransactionModal(false);  // モーダルを閉じる
      setInput(''); // フィールド変更時に入力値をクリア 
      
    };  


  // 印刷と会計前チェックで使っているフィールドをスキャン後チェックでも共用する。
  const buildQrCheckItems = (items) => items.map(item => ({
    inventoryId: item.inventory_fk_id ?? item.inventory_id ?? null,
    name: item.product_name,
    quantity: item.quantity,
    price: item.unit_price,
    subtotal: item.subtotal,
    discount: Number(item.discount || 0),
    jan: item.barcode || item.jan || "",
    set_sale: item.set_sale || 0,
    set_ids: item.set_ids ?? item.set_items ?? "",
    taxRate: Number(item.taxRate ?? item.tax_rate ?? taxRate?.tax_rate ?? 10),
    goodsType: item.goodsType ?? item.goods_type ?? 1,
    reducedTax: item.reducedTax,
    specialPackaging: item.specialPackaging,
    categoryCode: item.categoryCode,
    featureInfo: item.featureInfo,
    serialNumber: item.serialNumber,
    isTaxFreeEligible: item.isTaxFreeEligible,
  }));

  // バーコード/非在庫商品追加、数量・値引き・削除、免税方式変更後に再判定。
  // 会計確定直前の検証も残す（二重チェック）。
  useEffect(() => {
    if (sale_type !== "tax_exempt" || !salesList.length ||
        loading || taxFreeModeLoading || checkoutInProgressRef.current ||
        submittedReceiptId !== null || paymentCompletedHoldRef.current) {
      if (sale_type !== "tax_exempt" || !salesList.length) {
        qrLimitWarnedRef.current = false;
      }
      return;
    }

    let cancelled = false;
    // 連続スキャン中に何度もSET商品を取得しないよう少し待つ。
    const timer = setTimeout(async () => {
      try {
        let qrItems = buildQrCheckItems(salesList);
        if (taxFreeMode === "refund" || ENABLE_SMARTDETAX_LEGACY_FEATURES) {
          qrItems = await resolveSetJansForItems(
            qrItems,
            (id) => apiRequest(`inventory/inventory/${id}/`, "GET")
          );
        }
        if (cancelled || checkoutInProgressRef.current) return;

        // 10桁の仮レシート番号で保守的に容量を見積もる。
        prepareSmartDetaxReceipt({
          id: "9999999999",
          registerId: selectedRegister,
          storeId: storeId ?? userInfo?.store_id,
          date: new Date().toISOString(),
          isTaxFree: true,
          taxFreeMode,
          items: qrItems,
        });

        // 明細を減らして10個以下に戻ったら次の警告を許可する。
        qrLimitWarnedRef.current = false;
      } catch (error) {
        if (cancelled || checkoutInProgressRef.current) return;
        const msg = String(error?.message || error);
        const qrCount = msg.match(/QRが(\d+)個必要/);
        if (!qrCount) {
          // 商品編集中の一時的な欠損などはここでは警告しない。
          // 会計確定前チェックは従来どおり厳密に行う。
          console.debug("[SmartDetax] スキャン後チェック保留:", msg);
          return;
        }
        if (qrLimitWarnedRef.current) return;
        qrLimitWarnedRef.current = true;
        showAlert(
          `SmartDetax用QRが${qrCount[1]}個必要です（最大10個）。\n` +
          `商品を分けて会計してください。\n` +
          `まだ会計は確定していません。`
        );
      }
    }, 700);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [salesList, sale_type, taxFreeMode, taxRate, selectedRegister,
      storeId, userInfo?.store_id, loading, taxFreeModeLoading, submittedReceiptId]);

  const handleCompletePayment = async () => {
    // フロントでの免税下限チェック。スタッフが同日追加購入を明示確認した場合だけ通す。
    if (sale_type === "tax_exempt" && taxFreeRemaining > 0 && !taxFreeMinimumOverride) {
      showAlert(
        `免税基準（税抜5,000円）に達していません。\n` +
        `あと ¥${Math.ceil(taxFreeRemaining).toLocaleString()}（税抜）必要です。\n` +
        `本日同じお客様の追加購入の場合は「免税小計」を押してから、追加購入用の進むボタンをご利用ください。`
      );
      return; // checkout API へは送らない
    }

    if (checkoutInProgressRef.current || isSubmitting || submittedReceiptId !== null) {
      console.warn("⛔ 二重送信防止：処理中、またはすでに送信済みです");
      return;
    }

    checkoutInProgressRef.current = true;
    setIsSubmitting(true);
    let checkoutRequested = false;

    try {
    const discount = salesList.reduce((sum, item) => sum + item.discount, 0);
    const Tax = calculateSaleTax(totalAmount);

    const receiptPayload = {
      total_amount: totalAmount,
      tax_amount: Tax,
      discount: discount || 0,
      total_quantity: salesList.reduce((sum, item) => sum + item.quantity, 0),
      payment_method: paymentMethod,
      sale_type: sale_type === 'tax_exempt' ? 'tax_exempt' : 'normal',
      tax_exemption: tax_exemption,
      cash_received_amount: cashGiven - change,
      cashless_payment_amount: cardGiven,
      changeinfo: parseInt(changeinfo.toString().replace('¥', '').replace(',', '')) || 0,
      date: new Date().toISOString(),
    };

    const detailsPayload = salesList.map(item => ({
      inventory_id: item.inventory_fk_id || null,
      barcode: item.barcode || 0,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      subtotal: item.subtotal,
      condition: item.condition,
      special_price: item.special_price || 0,
      box_condition: Number(item.box_damage) || 0,
      set_sale: item.set_sale || 0,
      set_items: item.set_ids,
      category: item.category,
      discount: item.discount || 0,
    }));

    // =============================
    // SmartDetax QRの事前チェック（checkout POSTより前）
    // =============================
    // 実際の印刷データと同じフィールドを使い、同じQR生成処理で必要数を判定する。
    let qrPrintItems = buildQrCheckItems(salesList);

    if (sale_type === "tax_exempt") {
      // 従来/リファンドの特徴情報に入れるSET構成JANを事前取得する。
      if (taxFreeMode === "refund" || ENABLE_SMARTDETAX_LEGACY_FEATURES) {
        qrPrintItems = await resolveSetJansForItems(
          qrPrintItems,
          (id) => apiRequest(`inventory/inventory/${id}/`, "GET")
        );
      }

      // checkout前はDB採番されたレシート番号がないため10桁の仮番号を使用。
      // 実際の番号(1～10桁)より短く見積もらない安全側の判定。
      // 他の項目とQR分割条件は実印刷と同じ。
      const qrCheck = prepareSmartDetaxReceipt({
        id: "9999999999",
        registerId: selectedRegister,
        storeId: storeId ?? userInfo?.store_id,
        date: receiptPayload.date,
        isTaxFree: true,
        taxFreeMode,
        items: qrPrintItems,
      });
      console.log(
        `[SmartDetax] 会計前QR確認: ${qrCheck.smartDetaxPayloads.length}個 / 最大10個`
      );
      // 10個を超える場合や1明細が大きすぎる場合、prepareSmartDetaxReceiptが
      // ここで例外を出す。checkout APIはまだ呼ばれていないので売上は未保存。
    }

    // 事前チェックが通ったときだけ会計を確定する
    checkoutRequested = true;
    const result = await apiRequest("inventory/register/checkout/", "POST", {
      register_id: selectedRegister,
      store_id: storeId,
      receipt: receiptPayload,
      details: detailsPayload,
    });

      // checkout のレスポンス形式に合わせて、保存済みの伝票IDを取得する。
      // React state の receiptNo は使わず、ここで確定した値を印刷にも使う。
      const receiptIdCandidates = [
        result?.receipt_id,
        result?.receiptId,
        result?.receipt_number,
        result?.receipt_no,
        result?.receiptNo,
        result?.sales_receipt_id,
        result?.salesReceiptId,
        result?.receipt?.id,
        result?.receipt,
        result?.sales_receipt?.id,
        result?.data?.receipt_id,
        result?.data?.receipt?.id,
        result?.id,
      ];
      const receiptId = receiptIdCandidates
        .map(value => String(value ?? "").trim())
        .find(value => /^\d{1,10}$/.test(value));

      if (!receiptId) {
        console.error("[RegisterPage] checkoutのレシート番号を取得できません。API返却値:", result);
        // 会計がDBに登録済みの可能性があるため、重複会計を防ぐ。
        setSubmittedReceiptId("CHECKOUT_ID_MISSING");
        showAlert(
          "会計は保存された可能性がありますが、レシート番号を取得できませんでした。" +
          "再会計せず、取引履歴とcheckoutのAPI返却値を確認してください。"
        );
        return;
      }

      console.log("[RegisterPage] 確定レシート番号:", receiptId);
      setReceiptNo(receiptId);
      setSubmittedReceiptId(receiptId);

      // =============================
      // 現金を使った会計ならドロワーを即OPEN
      // =============================
      if (Number(cashGiven) > 0 && window.electronAPI?.openCashDrawer) {
        window.electronAPI.openCashDrawer().catch((err) => {
          console.error("キャッシュドロワーOPEN失敗:", err);
        });
      }

      // レシート印刷
      const taxBeforeExemption = calculateTax(totalAmount, taxRate);
      const taxAmount = calculateSaleTax(totalAmount);

      // 本来の税込金額
      const totalWithTax = totalAmount + taxBeforeExemption;

      // 実際に支払う金額
      const total = totalAmount + taxAmount;

      // qrPrintItems は会計確定前のQRチェックで使った同じ商品データ。
      // SET構成商品のJANもこの時点で解決済みなので、ここでは再取得しない。
      const receiptDataForPrint = {
        id: receiptId,               // checkout のDB確定ID（1〜10桁の数字）
        storeId: storeId ?? userInfo?.store_id, // SmartDetax QRヘッダー用の店舗番号
        registerId: selectedRegister,
        date: result?.receipt?.date ?? result?.date ?? new Date().toISOString(),

        items: qrPrintItems, // 元の商品名を保持。QR生成時のみ40byteに調整。SET構成JAN取得済み。

        totalItems: salesList.reduce(
          (sum, item) => sum + item.quantity,
          0
        ),

        subtotal: totalAmount,

        // 実際に徴収する税
        tax: taxAmount,

        // 免税しなかった場合の本来の税
        taxBeforeExemption: taxBeforeExemption,

        taxFreeAmount: tax_exemption,

        isTaxFree: sale_type === "tax_exempt",

        // ★追加
        taxFreeMode: taxFreeMode,

        // 本来の税込金額
        totalWithTax: totalWithTax,

        // 実際の支払額
        total: total,

        paidCash: cashGiven - change,
        paidCashless: cardGiven,
        change: change,
      };

      const customerCompleteData = {
        status: "complete",
        saleType: sale_type,
        isTaxFree: sale_type === "tax_exempt",
        taxFreeMode: taxFreeMode,

        items: salesList.map((item) => ({
          name: item.product_name,
          quantity: item.quantity,
          price: item.unit_price,
          subtotal: item.subtotal,
          discount: item.discount || 0,
          jan: item.barcode || item.jan || "",
        })),

        subtotal: totalAmount,
        discountTotal: discount || 0,
        tax: taxAmount,
        taxBeforeExemption: calculateTax(totalAmount, taxRate),
        taxIncludedSubtotal:
          totalAmount + calculateTax(totalAmount, taxRate),
        taxExemptionAmount:
          sale_type === "tax_exempt" &&
          taxFreeMode === "legacy"
            ? calculateTax(totalAmount, taxRate)
            : 0,

        total,
        paidCash: cashGiven,
        paidCashless: cardGiven,
        change,
      };

      completedDisplayRef.current = customerCompleteData;

      localStorage.setItem(
        "customerDisplayData",
        JSON.stringify(customerCompleteData)
      );
      // 会計完了表示を数秒残してから待機へ戻す
      setTimeout(() => {
        completedDisplayRef.current = null;

        localStorage.setItem(
          "customerDisplayData",
          JSON.stringify({
            status: "standby",
            saleType: "normal",
            isTaxFree: false,
            items: [],
            subtotal: 0,
            discountTotal: 0,
            tax: 0,
            taxBeforeExemption: 0,
            taxIncludedSubtotal: 0,
            taxExemptionAmount: 0,
            total: 0,
            paidCash: 0,
            paidCashless: 0,
            change: 0,
          })
        );
      }, 7000);

      // 通常レシートだけは、従来どおり自動印刷ONなら自動発行。
      // QR付きのSmartDetax伝票は自動印刷しない。
      if (autoReceiptPrintEnabled) {
        console.log("お客様レシートの自動印刷 実行");
        void printReceipt(receiptDataForPrint, showAlert);
      } else {
        console.log("お客様レシートの自動印刷 停止中");
      }

      // 免税・通常どちらも共通のAlertModalで表示する。
      // 元の「大きな緑色のレシート番号」とおつり強調表示は維持。
      const receiptAlertClass = Number(cashGiven) > 0
        ? "AlertModal-receiptNo AlertModal-highlightChange"
        : "AlertModal-receiptNo";

      if (receiptDataForPrint.isTaxFree) {
        // 会計は保存済み。QR生成が失敗しても二重会計にしない。
        let prepared = receiptDataForPrint;
        let qrError = null;
        try {
          prepared = prepareSmartDetaxReceipt(receiptDataForPrint);
        } catch (err) {
          qrError = err.message;
          console.error("[SmartDetax] QRデータ生成失敗:", err);
        }

        setLastReceiptData(qrError
          ? { ...prepared, smartDetaxPreparationError: qrError }
          : prepared);

        // QRの前/次・SmartDetax印刷ボタンで誤ってアラートを閉じないよう、
        // 通常販売用の「次の入力で閉じる」イベントは免税時には無効にする。
        lastReceiptAlertActiveRef.current = false;

        showAlert(`レシートNo: ${String(receiptId).padStart(6, "0")}`, {
          className: receiptAlertClass,
          smartDetax: {
            error: qrError,
            // AlertModalが必要に応じて画像を読み込みます。印刷はしません。
            loadImages: qrError ? null : () => getSmartDetaxImages(prepared),
            // OKの右の「SmartDetax印刷」を押した場合だけ別伝票を印刷。
            onPrint: qrError ? null : () => printSmartDetaxReceipt(prepared),
          },
        }).then(() => {
          resetAfterPaymentHold();
        });
      } else {
        setLastReceiptData(receiptDataForPrint);
        lastReceiptAlertActiveRef.current = true;
        showAlert(`レシートNo: ${String(receiptId).padStart(6, "0")}`, {
          className: receiptAlertClass,
        }).then(() => {
          lastReceiptAlertActiveRef.current = false;
          resetAfterPaymentHold();
        });
      }

      paymentCompletedHoldRef.current = true;
      staffResetTimerRef.current = setTimeout(() => {
        // スタッフ側のレジ入力は従来どおり30秒でリセット。
        // QR読み取り中もAlertModalは閉じず、OKまでは表示を維持。
        resetAfterPaymentHold();
      }, 30000);
    } catch (error) {
      console.error("会計処理／SmartDetax事前チェックのエラー:", error);
      if (!checkoutRequested) {
        // ここまでcheckout APIを呼んでいない＝売上は未登録。
        const errorText = String(error?.message || error);
        const qrCount = errorText.match(/QRが(\d+)個必要/);
        if (qrCount) {
          showAlert(
            `SmartDetax用QRが${qrCount[1]}個必要です（最大10個）。\n` +
            `同一商品を合算しても10個を超えています。\n` +
            `会計は確定していません。商品を分けて会計してください。`
          );
        } else {
          showAlert(
            `SmartDetax QRの事前チェックに失敗しました。\n${errorText}\n` +
            `会計は確定していません。`
          );
        }
      } else {
        // POST後のエラーはDB保存済みの可能性があるため、再会計は促さない。
        showAlert(
          `会計の処理中にエラーが発生しました。\n` +
          `売上が保存されている可能性があるため、再会計せず先に取引履歴を確認してください。\n` +
          String(error?.message || error)
        );
      }
    } finally {
      checkoutInProgressRef.current = false;
      setIsSubmitting(false);
    }
  };


  if (loading || taxFreeModeLoading) {
    return <div>読み込み中...</div>;
  }
  return (
    <>
    <CurrentTime setCurrentTime={setCurrentTime} />
    {showTaxFreeMinimumWarning && (
      <aside className="RegisterTaxFreeMinimum-warning" role="status" aria-live="polite">
        <div className="RegisterTaxFreeMinimum-heading">⚠ 免税基準額に達していません</div>
        <div className="RegisterTaxFreeMinimum-amounts">
          税抜現在：¥{Math.floor(taxFreeNetAmount).toLocaleString()} ／ 基準：¥5,000
        </div>
        <div className="RegisterTaxFreeMinimum-remaining">
          あと <strong>¥{Math.ceil(taxFreeRemaining).toLocaleString()}</strong>（税抜）で免税できます
        </div>
        <p className="RegisterTaxFreeMinimum-note">
          商品を追加すると不足額を自動更新します。<br />
          本日、同一店舗で同じお客様がすでに免税購入している場合のみ、下のボタンで進めます。<br />
          通常販売に切り替える場合は、既存の「小計」を押してください。
        </p>
        <div className="RegisterTaxFreeMinimum-actions">
          <button type="button" className="RegisterTaxFreeMinimum-proceed"
            onClick={handleTaxFreeAdditionalPurchase}>
            本日免税済みのお客様：追加購入としてこのまま進む
          </button>
        </div>
      </aside>
    )}
    <div className="registerPage">
      <div className="registerPage__leftColumn">
        <div className="registerPage__buttonArea">
          <div className='registerPage_selectedRegister'>{userInfo.register_name}［ID:{selectedRegister}］</div>
          <div className="registerPage__receiptToggle">
          <button
            type="button"
            className={
              autoReceiptPrintEnabled
                ? "registerPage__receiptToggleButton is-on"
                : "registerPage__receiptToggleButton is-off"
            }
            onClick={() => setAutoReceiptPrintEnabled((prev) => !prev)}
          >
            {autoReceiptPrintEnabled ? "レシート発行" : "レシート停止中"}
          </button>
        </div>
          <button
            onClick={() => {
              if (!lastReceiptData) {
                showAlert("再印刷できるデータがありません");
                return;
              }
              printReceipt(lastReceiptData, showAlert);
            }}
          >
            レシート再印刷
          </button>
          <button onClick={handleViewTransactionHistory}>取引履歴</button>
          <button onClick={() => {
            setShowReturnModal(true);
            setBarcodeFocusEnabled(false);
          }}>
            返品
          </button>
          <button onClick={openCashTransactionModal}>買取<br></br>出入金</button>
          <button onClick={regicancel}>取引中止</button>
          <button
            onClick={() => {
              if (window.electronAPI?.openCashDrawer) {
                window.electronAPI.openCashDrawer().catch((err) => {
                  console.error("キャッシュドロワーOPEN失敗:", err);
                  showAlert("キャッシュドロワーを開けませんでした");
                });
              } else {
                showAlert("キャッシュドロワー機能が利用できません");
              }
            }}
          >
            ドロワー<br />OPEN
          </button>
          <button onClick={handleButtonfocusClick} >FOCUS</button>
        </div>
      </div>

      <div className="registerPage__middleColumn">
        <NonStockTaxItemInput 
          addToSalesList={addToSalesList} 
          taxRate={taxRate} 
          setSalesList={setSalesList} 
          salesList={salesList} 
          input={input} 
          setInput={setInput}
          setSelectedItem={setSelectedItem}
          selectedItem={selectedItem} 
          openModal={openModal}
          currentTime={currentTime}
          dayOfWeek={dayOfWeek}
          formattedDate={formattedDate}
          formattedTime={formattedTime}
          setBarcodeFocusEnabled={setBarcodeFocusEnabled}

                  
          />

        <div className="registerPage__barcodeInputArea">
          <BarcodeInputForm addToSalesList={addToSalesList} ref={inputRef}/>
        </div>
        <div className="registerPage__salesDetailList">
          <SalesDetailList 
            salesList={salesList}
            updateSalesDetail={updateSalesDetail}
            removeSalesDetail={removeSalesDetail}
            setSelectedItem={setSelectedItem}
            setShowModal={setShowModal}
            selectedItem={selectedItem}
            showModal={showModal}
            openModal={openModal}
            setBarcodeFocusEnabled={setBarcodeFocusEnabled}
            
          />
        </div>
      </div>

      <div className="registerPage__rightColumn">
        <PaymentSummary
          totalAmount={totalAmount}
          salesList={salesList}
          sale_type={sale_type}
          taxFreeMode={taxFreeMode}
          cashGiven={cashGiven}
          cardGiven={cardGiven}
          change={change}
          taxRate={taxRate}
          changeinfo={changeinfo}
          calculateTax={calculateTax}
          setCashGiven={setCashGiven}
          setCardGiven={setCardGiven}
          setChange={setChange}          
        />
      </div>
      <div className="registerPage__PaymentActionsWithKeypad">
        <div className='registerPage__Keypad__box'>
        <PaymentActionsWithKeypad
          taxRate={taxRate}
          totalAmount={totalAmount}
          salesList={salesList}
          sale_type={sale_type}
          taxFreeMode={taxFreeMode}
          cashGiven={cashGiven}
          cardGiven={cardGiven}
          change={change}
          input={input}
          calculateTax={calculateTax}
          setSale_type={handleSubtotalSelection}
          setPaymentMethod={setPaymentMethod}
          setCashGiven={setCashGiven}
          setCardGiven={setCardGiven}
          setChange={setChange}
          setChangeinfo={setChangeinfo}
          handleCompletePayment={handleCompletePayment}
          setTax_exemption={setTax_exemption}
          setInput={setInput}
          handleButtonfocusClick={handleButtonfocusClick}
          isSalesDetailModalOpen={showModal}
        />
        </div>
      </div>
    </div>
  <div>
    <CashTransactionModal
      input={input}
      setInput={setInput}
      register={selectedRegister}
      showCashTransactionModal={showCashTransactionModal}
      closeCashTransactionModal={closeCashTransactionModal}
  />
    {showReturnModal && (
    <ReturnModal
      onClose={() => {
        setShowReturnModal(false);          // モーダルを閉じる
        setBarcodeFocusEnabled(true);       // フォーカスをバーコードに戻す
      }}
      register={selectedRegister}
      storeId={storeId}
      setShowReturnModal={setShowReturnModal}
      setBarcodeFocusEnabled={setBarcodeFocusEnabled}
    />
    )}
  </div>
  </>
  );
};

export default RegisterPage;
